/**
 * RasterHeatmapWebGLLayer.tsx
 * غلاف React لطبقة النسيج العالمي الخام (RasterHeatmapGLLayer).
 */

import { useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { blendFor, getPlaybackPosition, subscribePlayback } from '../../services/playbackClock';
import { RasterHeatmapGLLayer } from './webgl/RasterHeatmapGLLayer';
import { getWeatherInsertBeforeId, raiseParticlesAboveWeatherLayers, registerSeaPass, unregisterSeaPass } from './webgl/layerOrder';
import { getColorScaleVersion } from '../../services/colorScales';
import type { ForecastGridType } from '../../config/weatherLayers';

interface Props {
    id: string;
    url: string;
    urlNext?: string;   // الإطار التالي للاستيفاء الزمني
    blend?: number;     // نسبة المزج 0..1 (كسر الإطار)
    /**
     * فهرس الإطار الذي يمثّله `url` على الشريط الزمني. تمريره يفعّل **المزج المستمرّ**:
     * تشترك الطبقة في playbackClock وتقرأ كسراً عشرياً كاملاً كل نبضة رسم بدل ستّ درجات
     * مكمَّمة تصل عبر React. هذا هو الفرق بين انسياب الفيديو والقفز كل 500 مللي.
     */
    frameIndex?: number;
    /** يوقف المزج المستمرّ ويُبقي `blend` هو المصدر (طبقة الرياح حين يمنع تباعدُ المصدر المزجَ). */
    liveBlend?: boolean;
    type: ForecastGridType;
    opacity?: number;
    /** مدى ترميز النسيج إن خالف VALUE_RANGES (مثل سقوط الرياح إلى قناة R المرمَّزة 0..120). */
    valueRange?: [number, number];
    /** النسيج نظير 16-بت (‏R=الأعلى، G=الأدنى) لا رمادي 8-بت — يغيّر الفكّ فقط. */
    p16?: boolean;
    /** يُستدعى مرّة واحدة عند جهوز أوّل إطار — لتنعيم كشف الطبقة/قاعدة الخريطة. */
    onReady?: () => void;
    /** الطبقة بلا بيانات على الخادم (أوّل إطار 404) — راجع RasterHeatmapGLLayer.onUnavailable. */
    onUnavailable?: () => void;
}

export function RasterHeatmapWebGLLayer({ id, url, urlNext = '', blend = 0, frameIndex, liveBlend = false, type, opacity = 0.9, valueRange, p16 = false, onReady, onUnavailable }: Props) {
    const mapRef = useMapRef();
    const layerRef = useRef<RasterHeatmapGLLayer | null>(null);
    // أحدث valueRange — تُطبَّق عند إضافة الطبقة فعلياً (add تتأخر حتى جهوز الستايل،
    // فقد يفوتها effect المزامنة أدناه إن تغيّرت القيمة قبل الإضافة).
    const valueRangeRef = useRef(valueRange);
    valueRangeRef.current = valueRange;
    // أحدث onReady عبر ref كي لا يلتقط نداء onFirstFrame (يُضبط مرّة عند إنشاء الطبقة) نسخةً قديمة.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const onUnavailableRef = useRef(onUnavailable);
    onUnavailableRef.current = onUnavailable;
    // أحدث الإطار (رابط/تالٍ/مزج) عبر refs. إضافة الطبقة تتأخّر حتى جهوز الستايل؛ فإن تغيّر
    // الإطار (مزامنة الشريط إلى "الآن") قبل الإضافة، كانت الطبقة تُضاف على رابط الإنشاء القديم
    // (الإطار 0 = بداية اليوم) وتَعلَق عليه — فتظهر بيانات أبرد رغم أن المؤشّر على "الآن".
    const urlRef = useRef(url); urlRef.current = url;
    // كالمدى تماماً: الإضافة قد تتأخّر حتى جهوز الستايل، فتُقرأ الرايتان عندها من ref
    // بدل التقاط قيمة الإنشاء — وإلّا ظهرت طبقة الضغط بلا خطوط حتى أوّل تغيّر إطار.
    const flagsRef = useRef({ p16 }); flagsRef.current = { p16 };
    const urlNextRef = useRef(urlNext); urlNextRef.current = urlNext;
    // قيمة المزج الصحيحة الآن: من ساعة التشغيل حين يكون المزج المستمرّ مفعّلاً، وإلا من
    // الخاصية. تُقرأ عند إضافة الطبقة (قد تتأخّر حتى جهوز الستايل) كي لا تُضاف بمزج 0
    // بينما التشغيل جارٍ في منتصف إطار — وميض رجوع للخلف بمقدار نصف ساعة.
    const blendRef = useRef(blend);
    blendRef.current = (liveBlend && frameIndex !== undefined)
        ? blendFor(getPlaybackPosition(), frameIndex)
        : blend;

    useEffect(() => {
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();
        let cancelled = false;
        const layer = new RasterHeatmapGLLayer(id, type, url, opacity);
        layer.onFirstFrame = () => onReadyRef.current?.();
        layer.onUnavailable = () => onUnavailableRef.current?.();

        const add = () => {
            if (cancelled) return;
            try {
                layer.setValueRange(valueRangeRef.current);
                layer.setP16(flagsRef.current.p16);
                // مزامنة على أحدث إطار قبل الإضافة: setUrl قبل addLayer يضبط الرابط فقط (بلا
                // تحميل، إذ this.map لا يزال null)، ثم onAdd يُحمّل الرابط الأحدث — فلا تعلَق
                // الطبقة أبداً على إطار الإنشاء القديم إن كان الشريط قد قفز إلى "الآن".
                layer.setUrl(urlRef.current);
                layer.setNextUrl(urlNextRef.current);
                layer.setBlend(blendRef.current);
                if (!map.getLayer(id)) map.addLayer(layer, getWeatherInsertBeforeId(map));
                // تمريرة الماء: نفس موارد الطبقة، تُرسم فوق التضاريس فتقصر ظلّها على
                // اليابسة (راجع makeSeaPassLayer و enforceWeatherStackOrder).
                if (!map.getLayer(`${id}-sea`)) {
                    map.addLayer(layer.makeSeaPassLayer(), getWeatherInsertBeforeId(map));
                    // التسجيل شرط رفعها فوق التضاريس — getStyle() لا تُدرج الطبقات المخصّصة.
                    registerSeaPass(`${id}-sea`);
                }
                raiseParticlesAboveWeatherLayers(map);
                layerRef.current = layer;
            } catch (e) { console.error(`RasterHeatmapWebGLLayer(${id})`, e); }
        };
        let intervalId: number | undefined;
        const onStyleData = () => { if (map.isStyleLoaded()) { cleanupWait(); add(); } };
        const cleanupWait = () => { map.off('styledata', onStyleData); if (intervalId !== undefined) window.clearInterval(intervalId); };
        if (map.isStyleLoaded()) add();
        else { map.on('styledata', onStyleData); intervalId = window.setInterval(() => { if (map.isStyleLoaded()) { cleanupWait(); add(); } }, 150); }

        return () => {
            cancelled = true; cleanupWait(); layerRef.current = null;
            // تمريرة الماء أولاً: هي وكيل يقرأ من موارد الطبقة الأصلية، فإزالة الأصل
            // قبلها تترك إطاراً واحداً ترسم فيه من موارد محرَّرة.
            unregisterSeaPass(`${id}-sea`);
            try { if (map.getLayer(`${id}-sea`)) map.removeLayer(`${id}-sea`); } catch { /* تجاهل */ }
            try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* تجاهل */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef, id]);

    useEffect(() => { layerRef.current?.setType(type); }, [type]);
    useEffect(() => { layerRef.current?.setP16(p16); }, [p16]);
    useEffect(() => { layerRef.current?.setValueRange(valueRange); }, [valueRange?.[0], valueRange?.[1]]);
    useEffect(() => { layerRef.current?.setUrl(url); }, [url]);
    useEffect(() => { layerRef.current?.setNextUrl(urlNext); }, [urlNext]);
    useEffect(() => { if (!liveBlend) layerRef.current?.setBlend(blend); }, [blend, liveBlend]);

    /**
     * المزج المستمرّ — القناة التي تجعل الحركة تبدو فيديو لا شرائح.
     *
     * تشترك الطبقة مباشرةً في ساعة التشغيل وتضبط uniform المزج كل نبضة رسم، متجاوزةً
     * React تماماً. الكلفة صفر تقريباً: `setBlend` يقارن القيمة ثم يطلب إعادة رسم،
     * والشيدر يمزج نسيجين على الـ GPU وهو يفعل ذلك أصلاً عند أي blend > 0.
     *
     * الاشتراك يُعاد بناؤه عند تغيّر `frameIndex` وحده (لا عند كل رسم)، وnull تعني
     * «لا فهرس معلوم» فنترك القيمة للخاصية العادية.
     */
    useEffect(() => {
        if (!liveBlend || frameIndex === undefined) return;
        return subscribePlayback((p) => {
            layerRef.current?.setBlend(blendFor(p, frameIndex));
        });
    }, [liveBlend, frameIndex]);
    useEffect(() => { layerRef.current?.setOpacity(opacity); }, [opacity]);

    // شريط الألوان على GPU يُبنى مرّة عند أوّل رسم فقط (rampDirty)؛ لا آلية اشتراك جاهزة
    // لتغيّر STOPS_REGISTRY (مثل تعديل حيّ من لوحة الأدمن على نفس التبويب). فحص خفيف كل
    // ثانيتين كافٍ (مقارنة عدد صحيح فقط) بدل بناء نظام أحداث كامل لحالة نادرة.
    useEffect(() => {
        let lastVersion = getColorScaleVersion();
        const id = window.setInterval(() => {
            const v = getColorScaleVersion();
            if (v !== lastVersion) {
                lastVersion = v;
                layerRef.current?.markColorsDirty();
            }
        }, 2000);
        return () => window.clearInterval(id);
    }, []);

    return null;
}
